import { useEffect, useState } from 'react';
import { Camera, Check, CheckCircle2 } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { NoCargo } from './NoCargo';
import { useFallo } from '../lib/useFallo';

/**
 * Cuadros de VALIDACIÓN de Claim Review: sé de un vistazo qué falta por validar y quién lo hizo.
 * Cada cuadro: Policy / LOR / Weather / Existing-New / Intake. Verde = hecho (auto o a mano),
 * ámbar = pendiente. Click en el cuadro → baja a su sección (anchor). Botón ✓/○ = marcar a mano.
 */
type Item = {
  key: 'policy' | 'lor' | 'weather' | 'existing_new' | 'intake' | 'photos';
  status: 'pending' | 'done' | 'na';
  auto: boolean;
  done_by_name: string | null;
  done_at: string | null;
  note: string | null;
  hint: string | null;
};

// A qué anchor baja cada cuadro dentro de Claim Review.
const ANCHOR: Record<Item['key'], string> = {
  policy: 'val-policy',
  lor: 'val-lor',
  weather: 'val-weather',
  existing_new: 'val-decision',
  intake: 'val-intake',
  photos: 'val-photos',
};

export function ValidationChecklist({ claimId }: { claimId: string }) {
  const { t } = useI18n();
  const [items, setItems] = useState<Item[]>([]);
  const [busy, setBusy] = useState('');
  // Modal "What's wrong with photos?" → al confirmar crea un item en Info Pending.
  const [photoOpen, setPhotoOpen] = useState(false);
  const [photoReason, setPhotoReason] = useState('');
  const [photoBusy, setPhotoBusy] = useState(false);

  const { fallo, marcar, limpiar } = useFallo();
  const load = () => api.get<{ items: Item[] }>(`/api/claims/${claimId}/validations`).then((r) => setItems(r.items)).then(limpiar).catch(marcar);
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [claimId]);

  const label = (k: Item['key']): string => t(`val.${k === 'existing_new' ? 'existingNew' : k}`);

  async function toggle(it: Item) {
    // PHOTOS: no es un toggle normal — abre el modal "what's wrong with photos?"; al confirmar se
    // crea el item en Info Pending ("Photos needed - <razón>") y se marca la validación.
    if (it.key === 'photos') { setPhotoReason(''); setPhotoOpen(true); return; }
    setBusy(it.key);
    try { await api.patch(`/api/claims/${claimId}/validations/${it.key}`, { status: it.status === 'done' ? 'pending' : 'done' }); await load(); }
    catch { /* */ } finally { setBusy(''); }
  }

  /** Confirma el problema de fotos → crea el item en Info Pending + marca la validación photos. */
  async function submitPhotoIssue() {
    const reason = photoReason.trim();
    if (!reason) return;
    setPhotoBusy(true);
    try {
      await api.post(`/api/claims/${claimId}/info-pending`, { label: `Photos needed - ${reason}`, sourceStage: 'Claim Review' });
      await api.patch(`/api/claims/${claimId}/validations/photos`, { status: 'done', note: reason });
      setPhotoOpen(false); setPhotoReason(''); await load();
    } catch { /* */ } finally { setPhotoBusy(false); }
  }
  function goTo(k: Item['key']) {
    const el = document.getElementById(ANCHOR[k]);
    if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.style.transition = 'background .2s'; el.style.background = 'var(--accent-soft)'; setTimeout(() => { el.style.background = ''; }, 1200); }
  }

  const doneN = items.filter((i) => i.status === 'done').length;

  return (
    <div className="card" style={{ display: 'grid', gap: 8 }}>
      {fallo && <NoCargo onRetry={load} compact />}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span className="muted" style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.4 }}><CheckCircle2 size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('val.title')}</span>
        <span className="badge" style={{ fontSize: 'var(--fs-2xs)', background: doneN === items.length && items.length ? 'var(--green-soft)' : 'var(--surface-2)', color: doneN === items.length && items.length ? 'var(--green)' : 'var(--muted)' }}>{doneN}/{items.length}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
        {items.map((it) => {
          const done = it.status === 'done';
          return (
            <div key={it.key} onClick={() => goTo(it.key)}
              style={{ cursor: 'pointer', border: `1px solid ${done ? 'var(--green)' : 'var(--line)'}`, borderRadius: 10, padding: '8px 10px', background: done ? 'var(--green-soft)' : 'var(--surface-2)', display: 'grid', gap: 3, minWidth: 0 }}
              title={t('val.goToSection')}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {/* Toggle manual: check verde / círculo vacío. */}
                <button onClick={(e) => { e.stopPropagation(); toggle(it); }} disabled={busy === it.key}
                  style={{ width: 18, height: 18, borderRadius: '50%', flexShrink: 0, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--fs-2xs)', lineHeight: 1,
                    background: done ? 'var(--green)' : 'transparent', color: done ? '#fff' : 'var(--muted)', border: done ? 'none' : '1.5px solid var(--line)' }}
                  title={done ? t('val.markPending') : t('val.markDone')}>{done ? <Check size={12} strokeWidth={1.75} /> : ''}</button>
                <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label(it.key)}</span>
              </div>
              <div className="muted" style={{ fontSize: 'var(--fs-2xs)', minHeight: 13 }}>
                {done
                  ? (it.auto ? `${t('val.auto')}${it.hint ? ` · ${it.hint}` : ''}` : `${it.done_by_name || '—'}`)
                  : t('val.pending')}
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal "What's wrong with photos?" → crea el item en Info Pending. */}
      {photoOpen && (
        <div onClick={() => setPhotoOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 4000, display: 'grid', placeItems: 'center', padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} className="card" style={{ maxWidth: 460, width: '100%', display: 'grid', gap: 10 }}>
            <div style={{ fontWeight: 'var(--fw-bold)', fontSize: 'var(--fs-base)' }}><Camera size={15} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('val.photosWrongTitle')}</div>
            <textarea className="input" autoFocus rows={3} placeholder={t('val.photosWrongPh')} value={photoReason}
              onChange={(e) => setPhotoReason(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submitPhotoIssue(); }} />
            <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('val.photosWrongHint')}</div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn ghost sm" onClick={() => setPhotoOpen(false)}>{t('common.cancel')}</button>
              <button className="btn sm" disabled={photoBusy || !photoReason.trim()} onClick={submitPhotoIssue}>{t('val.photosWrongAdd')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
